import { useCallback, useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { type FilterState, type PetType, VALID_PROPERTY_TYPE_VALUES, VALID_RENTAL_TYPE_VALUES } from './filterTypes';
import type { BedsFilterValue } from './bedroomMappings';
import type { BathsFilterValue } from './bathroomMappings';
import { displayedTypes, type FilterPill } from './filterLabels';
import { abbreviatePrice } from './priceFormat';

/**
 * Locale-aware filter button label helpers.
 *
 * Scope: only the compact labels displayed on the FilterBar popovers /
 * segmented controls. The full pill list (getActiveFilterPills) is still
 * generated by the untranslated helpers in filterLabels.ts — translating
 * every pill category (amenities, property types, rental types, etc.)
 * will be a later pass.
 */
export function useFilterLabels() {
  const tFilters = useTranslations('filters');
  const tListing = useTranslations('listing');
  const locale = useLocale();
  const numberLocale = locale === 'es' ? 'es' : 'en-US';

  const formatCurrency = useCallback(
    (value: number) => `$${value.toLocaleString(numberLocale)}`,
    [numberLocale],
  );

  // "1 bed" / "2 beds" → "bed" / "beds"
  const bedsWord = useCallback(
    (count: number) => tListing('bed', { count }).replace(/^\d+\s*/, ''),
    [tListing],
  );
  const bathsWord = useCallback(
    (count: number) => tListing('bath', { count }).replace(/^\d+\s*/, ''),
    [tListing],
  );

  const getPriceButtonLabel = useCallback(
    (price: FilterState['price']): string => {
      // Trigger label uses abbreviated prices ($2.5k, $900) so width stays
      // stable as the slider moves. Phrasing varies by which bounds are set:
      //   neither → "Any price"
      //   max only → "Under $X"
      //   min only → "Over $X"
      //   both    → "$X - $Y"
      const { min, max } = price;
      const hasMin = min !== null && min > 0;
      const hasMax = max !== null && max > 0;
      if (!hasMin && !hasMax) return tFilters('anyPrice');
      if (hasMin && hasMax) {
        return tFilters('priceRangeFormat', {
          min: abbreviatePrice(min!),
          max: abbreviatePrice(max!),
        });
      }
      if (hasMax) return tFilters('priceUnder', { price: abbreviatePrice(max!) });
      return tFilters('priceOver', { price: abbreviatePrice(min!) });
    },
    [tFilters],
  );

  const getBedsButtonLabel = useCallback(
    (value: BedsFilterValue): string => {
      if (value.selected === 'any') return tFilters('allBeds');
      const num = Number(value.selected);
      if (value.max !== null && num === value.max) return tListing('bed', { count: num });
      if (value.max !== null) return `${num}–${value.max} ${bedsWord(2)}`;
      return `${value.selected}+ ${bedsWord(2)}`;
    },
    [tFilters, tListing, bedsWord],
  );

  const getBathsButtonLabel = useCallback(
    (value: BathsFilterValue): string => {
      if (value === 'any') return tFilters('allBaths');
      return `${value}+ ${bathsWord(2)}`;
    },
    [tFilters, bathsWord],
  );

  const getPetsButtonLabel = useCallback(
    (pets: Array<PetType>): string => {
      if (pets.length === 0) return tFilters('pets');
      if (pets.length === 2) return tFilters('allPets');
      return pets
        .map((v) => (v === 'cats' ? tFilters('cats') : v === 'dogs' ? tFilters('dogs') : v))
        .join(', ');
    },
    [tFilters],
  );

  const tModal = useTranslations('filterModal');
  const tCommon = useTranslations('common');

  const priceLabel = useCallback(
    (price: FilterState['price']): string | null => {
      const { min, max } = price;
      if (min === null && max === null) return null;
      if (min !== null && max === null) return `${formatCurrency(min)}+`;
      if (min === null && max !== null) return `${tFilters('noMin')} ${tFilters('to')} ${formatCurrency(max)}`;
      return `${formatCurrency(min!)}–${formatCurrency(max!)}`;
    },
    [formatCurrency, tFilters],
  );

  const bedsPillLabel = useCallback(
    (bedrooms: FilterState['bedrooms']): string | null => {
      if (bedrooms.min === null) return null;
      if (bedrooms.min === 0 && bedrooms.max === 0) return tListing('studio');
      if (bedrooms.min !== null && bedrooms.min === bedrooms.max) return tListing('bed', { count: bedrooms.min });
      if (bedrooms.max !== null) return `${bedrooms.min}–${bedrooms.max} ${bedsWord(2)}`;
      return `${bedrooms.min}+ ${bedsWord(2)}`;
    },
    [tListing, bedsWord],
  );

  const bathsPillLabel = useCallback(
    (bathrooms: FilterState['bathrooms']): string | null => {
      if (bathrooms.min === null) return null;
      return `${bathrooms.min}+ ${bathsWord(2)}`;
    },
    [bathsWord],
  );

  const sqftPillLabel = useCallback(
    (sqft: FilterState['sqft']): string | null => {
      const { min, max } = sqft;
      if (min === null && max === null) return null;
      if (min !== null && max === null) return tListing('sqft', { value: min }) + '+';
      if (min === null && max !== null) return `${tFilters('to')} ${tListing('sqft', { value: max })}`;
      return `${tListing('sqftRange', { min: min!, max: max! })}`;
    },
    [tListing, tFilters],
  );

  const translatePet = useCallback(
    (value: string) => (value === 'cats' ? tFilters('cats') : value === 'dogs' ? tFilters('dogs') : value),
    [tFilters],
  );

  const translateAmenity = useCallback(
    (value: string): string => {
      const amenityKeys: Record<string, string> = {
        cooling: 'amAC',
        heating: 'amHeating',
        parking: 'amParking',
        gatedEntry: 'amGatedEntry',
        doorman: 'amDoorman',
        fitnessCenter: 'amGym',
        swimmingPool: 'amPool',
        dishwasher: 'amDishwasher',
      };
      const key = amenityKeys[value];
      return key ? tModal(key as Parameters<typeof tModal>[0]) : value;
    },
    [tModal],
  );

  const translateLaundry = useCallback(
    (value: string): string => {
      if (value === 'inUnit') return tModal('amInUnitLaundry');
      if (value === 'shared') return tModal('amSharedLaundry');
      return value;
    },
    [tModal],
  );

  const translateRentalType = useCallback(
    (value: string): string => {
      const map: Record<string, string> = {
        rental: 'rentalRegular',
        room: 'rentalRoom',
        sublet: 'rentalSublet',
        corporate: 'rentalCorporate',
      };
      const key = map[value];
      return key ? tModal(key as Parameters<typeof tModal>[0]) : value;
    },
    [tModal],
  );

  const translatePropertyType = useCallback(
    (value: string): string => {
      const map: Record<string, string> = {
        apartment: 'ptApt',
        condo: 'ptCondo',
        duplex: 'ptDuplex',
        house: 'ptHouse',
        townhouse: 'ptTown',
      };
      const key = map[value];
      return key ? tModal(key as Parameters<typeof tModal>[0]) : value;
    },
    [tModal],
  );

  const translateAdditionalOpt = useCallback(
    (value: string): string => {
      const map: Record<string, string> = {
        requiresPhotos: 'addHasPhotos',
        requiresPrice: 'addHasPrice',
        forRentByOwner: 'addForRentByOwner',
        hasOffers: 'addHasOffers',
        acceptingApplications: 'addAcceptingApplications',
      };
      const key = map[value];
      return key ? tModal(key as Parameters<typeof tModal>[0]) : value;
    },
    [tModal],
  );

  const translateRestriction = useCallback(
    (value: string): string => {
      const map: Record<string, string> = {
        income: 'restrIncome',
        senior: 'restrSenior',
        student: 'restrStudent',
        military: 'restrMilitary',
        none: 'restrHideAll',
      };
      const key = map[value];
      return key ? tModal(key as Parameters<typeof tModal>[0]) : value;
    },
    [tModal],
  );

  const translateCreatedWithin = useCallback(
    (value: string): string => {
      const map: Record<string, string> = {
        hour: 'lastHour',
        day: 'lastDay',
        week: 'lastWeek',
        month: 'lastMonth',
      };
      const key = map[value];
      return key ? tModal(key as Parameters<typeof tModal>[0]) : value;
    },
    [tModal],
  );

  const moveInDateLabel = useCallback(
    (md: FilterState['moveInDate']): string | null => {
      const moveInBy = md.end ?? md.start;
      return moveInBy ? tModal('moveInByLabel', { date: moveInBy }) : null;
    },
    [tModal],
  );

  const orderByLabel = useCallback(
    (value: string): string => {
      const map: Record<string, string> = {
        activated: 'sortNewest',
        weekViews: 'sortMostPopular',
        highPrice: 'sortHighPrice',
        lowPrice: 'sortLowPrice',
      };
      const k = map[value];
      // search.* namespace already covers sort labels
      return k ? `${tCommon('search')}: ${value}` : value;
    },
    [tCommon],
  );

  const getActiveFilterPills = useCallback(
    (filters: FilterState): Array<FilterPill> => {
      const pills: Array<FilterPill> = [];

      const p = priceLabel(filters.price);
      if (p) pills.push({ key: 'price', label: p });

      const b = bedsPillLabel(filters.bedrooms);
      if (b) pills.push({ key: 'bedrooms', label: b });

      const ba = bathsPillLabel(filters.bathrooms);
      if (ba) pills.push({ key: 'bathrooms', label: ba });

      filters.pets.forEach((value) => pills.push({ key: 'pets', value, label: translatePet(value) }));
      filters.laundry.forEach((value) =>
        pills.push({ key: 'laundry', value, label: translateLaundry(value) }),
      );
      filters.amenities.forEach((value) =>
        pills.push({ key: 'amenities', value, label: translateAmenity(value) }),
      );
      if (filters.furnished) pills.push({ key: 'furnished', label: tModal('amFurnished') });
      // All members selected means "show all", which is not a filter — matches
      // the static builder that `activeFilterCount` reads.
      displayedTypes(filters.propertyTypes, VALID_PROPERTY_TYPE_VALUES).forEach((value) =>
        pills.push({ key: 'propertyTypes', value, label: translatePropertyType(value) }),
      );

      const sl = sqftPillLabel(filters.sqft);
      if (sl) pills.push({ key: 'sqft', label: sl });

      if (filters.createdWithin) {
        pills.push({ key: 'createdWithin', label: translateCreatedWithin(filters.createdWithin) });
      }

      displayedTypes(filters.rentalTypes, VALID_RENTAL_TYPE_VALUES).forEach((value) =>
        pills.push({ key: 'rentalTypes', value, label: translateRentalType(value) }),
      );

      if (filters.restrictions !== null) {
        pills.push({ key: 'restrictions', label: translateRestriction(filters.restrictions) });
      }

      const ml = moveInDateLabel(filters.moveInDate);
      if (ml) pills.push({ key: 'moveInDate', label: ml });

      if (filters.keywords.trim()) {
        pills.push({ key: 'keywords', label: `"${filters.keywords.trim()}"` });
      }

      filters.additionalOpts.forEach((value) =>
        pills.push({ key: 'additionalOpts', value, label: translateAdditionalOpt(value) }),
      );

      if (filters.commute) {
        pills.push({
          key: 'commute',
          label: `${tModal('commute')}: ${tModal('commuteMinutes', { minutes: filters.commute.minutes })}`,
        });
      }

      if (filters.orderBy) {
        pills.push({ key: 'orderBy', label: orderByLabel(filters.orderBy) });
      }

      return pills;
    },
    [
      priceLabel,
      bedsPillLabel,
      bathsPillLabel,
      sqftPillLabel,
      translatePet,
      translateLaundry,
      translateAmenity,
      translatePropertyType,
      translateRentalType,
      translateRestriction,
      translateCreatedWithin,
      translateAdditionalOpt,
      moveInDateLabel,
      orderByLabel,
      tModal,
    ],
  );

  return useMemo(
    () => ({
      getPriceButtonLabel,
      getBedsButtonLabel,
      getBathsButtonLabel,
      getPetsButtonLabel,
      getActiveFilterPills,
    }),
    [
      getPriceButtonLabel,
      getBedsButtonLabel,
      getBathsButtonLabel,
      getPetsButtonLabel,
      getActiveFilterPills,
    ],
  );
}
